export type BackupsChartsActiveTab = 'quota' | 'history' | 'quotaHistory';

export type StorageMbYAxisScale = {
  min: number;
  max: number;
  tickAmount: number;
  step: number;
};

export const backupsDonutColors = [
  '#3182CE',
  '#E53E3E',
  '#DD6B20',
  '#38A169',
  '#805AD5',
  '#319795',
  '#D53F8C',
  '#2C5282',
];

const STORAGE_CHART_Y_AXIS_MIN_INTERVALS = 4;
const STORAGE_CHART_Y_AXIS_MAX_INTERVALS = 6;

const NICE_MB_STEP_MULTIPLIERS = [1, 2, 2.5, 5, 10] as const;

function pickNiceMbAxisStep(rawStep: number): number {
  if (rawStep <= 0) return 1;

  const power = Math.floor(Math.log10(rawStep));
  const base = 10 ** power;
  const match = NICE_MB_STEP_MULTIPLIERS.find((multiplier) => multiplier * base >= rawStep);

  return match === undefined ? 10 * base : match * base;
}

function getNextNiceMbAxisStep(step: number): number {
  const power = Math.floor(Math.log10(step));
  const base = 10 ** power;
  const multiplier = step / base;

  if (multiplier < 2) return 2 * base;
  if (multiplier < 2.5) return 2.5 * base;
  if (multiplier < 5) return 5 * base;
  if (multiplier < 10) return 10 * base;
  return 10 * base * 10;
}

export function getInitialQuotaFrom(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 11);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function getInitialQuotaTo(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function getMonthOptions(): string[] {
  const options: string[] = [];
  const now = new Date();
  const startYear = now.getFullYear() - 5;
  const endYear = now.getFullYear() + 1;

  for (let year = startYear; year <= endYear; year++) {
    for (let month = 0; month < 12; month++) {
      const d = new Date(year, month, 1);
      options.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
  }

  return options;
}

export function formatMonthLabel(value: string): string {
  const [y, m] = value.split('-').map(Number);
  const date = new Date(y, (m ?? 1) - 1, 1);
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

/** Y-axis from 0 with nice tick steps sized to the API peak (MB). */
export function getStorageMbYAxisScale(peakMb: number): StorageMbYAxisScale {
  if (peakMb <= 0) {
    return { min: 0, max: 10, tickAmount: 5, step: 2 };
  }

  let bestScale: StorageMbYAxisScale | null = null;

  for (
    let intervals = STORAGE_CHART_Y_AXIS_MIN_INTERVALS;
    intervals <= STORAGE_CHART_Y_AXIS_MAX_INTERVALS;
    intervals += 1
  ) {
    let step = pickNiceMbAxisStep(peakMb / intervals);
    let max = step * intervals;

    while (max < peakMb) {
      step = getNextNiceMbAxisStep(step);
      max = step * intervals;
    }

    if (!bestScale || max < bestScale.max) {
      bestScale = { min: 0, max, tickAmount: intervals, step };
    }
  }

  return bestScale ?? { min: 0, max: 10, tickAmount: 5, step: 2 };
}

export function formatStorageMbAxisLabel(value: number, step: number): string {
  let display: string;
  if (step < 1) {
    display = value.toFixed(1);
  } else if (step < 10 && !Number.isInteger(value)) {
    display = value.toFixed(1);
  } else if (step < 10) {
    display = String(value);
  } else {
    display = String(Math.round(value));
  }

  return `${display} MB`;
}
